/**
 * ConsoleAdapter.ts - ConsoleAdapter
 * 
 * CHANGE HISTORY:
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { LogLevel } from '../models/LogEntry.js';
import { type Actr } from '@opcentral/typr-lib';

/**
 * ConsoleAdapter class
 */
export class ConsoleAdapter {
    options: Record<string, unknown>;
  colors: unknown;
  minLevel: number;
  timestamps: boolean;

  constructor(options = {}) {
    this.options = {
      minLevel: (options as any).minLevel !== undefined ? (options as any).minLevel : LogLevel.INFO,
      colors: (options as any).colors !== false,
      timestamps: (options as any).timestamps !== false,
      ...options
    };
    
    this.minLevel = (options as any).minLevel ?? LogLevel.INFO;
    this.timestamps = (options as any).timestamps ?? true;
    
    this.colors = {
      TRACE: '\x1b[90m',    // Gray
      DEBUG: '\x1b[36m',    // Cyan
      INFO: '\x1b[32m',     // Green
      WARN: '\x1b[33m',     // Yellow
      ERROR: '\x1b[31m',    // Red
      FATAL: '\x1b[35m',    // Magenta
      RESET: '\x1b[0m'
    };
  }
  
  /**
   * Write log entry
   */
  write(logEntry: any): void {
    if ((logEntry as any).level < this.minLevel) {
      return;
    }
    
    const message = this.format(logEntry);
    
    // Use appropriate console method
    switch ((logEntry as any).level) {
      case LogLevel.TRACE:
      case LogLevel.DEBUG:
        console.debug(message);
        break;
      case LogLevel.INFO:
        console.info(message);
        break;
      case LogLevel.WARN:
        console.warn(message);
        break;
      case LogLevel.ERROR:
      case LogLevel.FATAL:
        console.error(message);
        if ((logEntry as any).error) {
          console.error((logEntry as any).error);
        }
        break;
      default:
        console.log(message);
    }
  }
  
  /**
   * Format log entry
   */
  format(logEntry: any): string {
    let message = '';
    
    if ((this.options as any).colors && typeof process !== 'undefined') {
      const color = (this.colors as any)[(logEntry as any).levelName] || (this.colors as any).RESET;
      message = `${color}${(logEntry as any).toConsoleString()}${(this.colors as any).RESET}`;
    } else {
      message = (logEntry as any).toConsoleString();
    }
    
    return message;
  }

  private getLevelString(level: number): string {
    switch (level) {
      case LogLevel.TRACE:
        return 'TRACE';
      case LogLevel.DEBUG:
        return 'DEBUG';
      case LogLevel.INFO:
        return 'INFO';
      case LogLevel.WARN:
        return 'WARN';
      case LogLevel.ERROR:
        return 'ERROR';
      case LogLevel.FATAL:
        return 'FATAL';
      default:
        return '';
    }
  }
}

export default ConsoleAdapter;
